import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeSlash, ArrowLeft, EnvelopeSimple } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { pixelCompleteRegistration, pixelStartTrial } from '../utils/pixel';
import { getScanContext } from '../utils/scanSession';
import { track } from '../components/landing/shared';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const RegisterPage = () => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const scan = getScanContext();

  const pwOk = password.length >= 8;

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!pwOk) { setError('Password must be at least 8 characters'); return; }
    if (!name.trim()) { setError('Please enter your name'); return; }
    setLoading(true);
    track('signup_started', { source: scan ? 'scanner' : 'direct' });
    try {
      await axios.post(`${API}/api/auth/send-verification-code`, { email: email.trim(), name: name.trim() });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) { setError('Please enter the 6-digit code from your email'); return; }
    setLoading(true);
    try {
      await register(email.trim(), password, name.trim(), code.trim());
      pixelCompleteRegistration();
      pixelStartTrial();
      track('signup_completed', { source: scan ? 'scanner' : 'direct' });
      navigate(scan ? '/strategy-ready' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : 'Invalid code or account creation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError('');
    try {
      await axios.post(`${API}/api/auth/send-verification-code`, { email: email.trim(), name: name.trim() });
      setError('');
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft size={18} /><span className="text-sm">Back to home</span>
        </Link>

        <div className="bg-zinc-900/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#c8f060] flex items-center justify-center mx-auto mb-4">
              <span className="text-black font-bold text-lg">S</span>
            </div>
            <h1 className="text-2xl font-medium font-['Instrument_Serif',serif] mb-2">
              {step === 1 ? 'Create your account' : 'Check your email'}
            </h1>
            <p className="text-zinc-400 text-sm">
              {step === 1 ? 'Start with 20 free credits' : `Enter the code we sent to ${email}`}
            </p>
          </div>

          {scan && (
            <div data-testid="register-scan-banner" className="rounded-xl p-4 mb-6" style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.2)' }}>
              <p className="text-sm" style={{ color: '#c8f060' }}>
                We saved your scan of <strong>{scan.host || scan.url}</strong>.
              </p>
              <p className="text-xs mt-1 text-zinc-400">Your strategy will be ready as soon as you create your account — no need to enter your website again.</p>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
              <p className="text-red-400 text-sm" data-testid="error-message">{error}</p>
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.form key="step1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onSubmit={handleSendCode} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Full name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} data-testid="name-input"
                    placeholder="John Doe" required
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="email-input"
                    placeholder="you@yourdomain.com" required
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      data-testid="password-input" placeholder="Min 8 characters" required minLength={8}
                      className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 pr-12 focus:outline-none focus:ring-1 focus:ring-white/30" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-[11px] mt-1.5" style={{ color: pwOk ? '#c8f060' : 'rgba(245,240,232,0.3)' }}>
                    {pwOk ? '✓ Password looks good' : 'Min 8 characters'}
                  </p>
                </div>
                <button type="submit" disabled={loading || !pwOk} data-testid="register-submit-btn"
                  className="w-full bg-[#c8f060] text-black hover:opacity-90 transition-all rounded-full font-bold py-3 disabled:opacity-40">
                  {loading ? 'Sending code...' : 'Create Account'}
                </button>
              </motion.form>
            ) : (
              <motion.form key="step2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onSubmit={handleVerifyAndRegister} className="space-y-5">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[rgba(200,240,96,0.06)] border border-[rgba(200,240,96,0.1)] flex items-center justify-center mx-auto mb-4">
                    <EnvelopeSimple size={24} style={{ color: '#c8f060' }} />
                  </div>
                </div>
                <div>
                  <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    data-testid="verification-code-input" placeholder="Enter 6-digit code" maxLength={6} autoFocus
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white text-center text-xl font-mono tracking-[6px] placeholder:text-zinc-600 placeholder:tracking-normal placeholder:text-sm px-4 py-4 focus:outline-none focus:ring-1 focus:ring-[#c8f060]/30" />
                </div>
                <button type="submit" disabled={loading || code.length < 6} data-testid="verify-and-register-btn"
                  className="w-full bg-[#c8f060] text-black hover:opacity-90 transition-all rounded-full font-bold py-3 disabled:opacity-40">
                  {loading ? 'Creating account...' : 'Verify & Create Account'}
                </button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={() => { setStep(1); setCode(''); setError(''); }} className="text-zinc-500 hover:text-white transition-colors">
                    Change email
                  </button>
                  <button type="button" onClick={handleResendCode} disabled={resending} className="text-[#c8f060] hover:underline disabled:opacity-50">
                    {resending ? 'Sending...' : 'Resend code'}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          <div className="mt-6 text-center">
            <p className="text-zinc-400 text-sm">
              Already have an account?{' '}
              <Link to="/login" data-testid="login-link" className="text-white hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
